import { useState, useEffect, useCallback } from 'react';
import { useUpdateNotificationsPreferencesMutation } from 'services/notificationService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { NotificationChannel, NotificationType } from '../types/notificationTypes';

export const useNotificationToggle = (
  initialEnabled: boolean,
  memberInitialEnabled: boolean = false,
  memberSmsEnabled: boolean = false,
  onErrorCallback: () => void,
  onSuccessCallback?: () => void,
) => {
  const user = useLoggedInUser();
  const memberUuid = user?.uuid ?? '';

  const [notificationsEnabled, setNotificationsEnabled] = useState(
    initialEnabled || memberInitialEnabled,
  );
  const [chatNotificationEnabled, setChatNotificationEnabled] = useState(initialEnabled);
  const [memberNotificationEnabled, setMemberNotificationEnabled] = useState(memberInitialEnabled);

  const [
    updateNotificationsPreferences,
    { error: updateNotificationsPreferencesError, isLoading },
  ] = useUpdateNotificationsPreferencesMutation();

  // Sync toggle state when the fetched notification settings resolve (they start undefined/false).
  useEffect(() => {
    setChatNotificationEnabled(initialEnabled);
  }, [initialEnabled]);

  useEffect(() => {
    setMemberNotificationEnabled(memberInitialEnabled);
  }, [memberInitialEnabled]);

  useEffect(() => {
    setNotificationsEnabled(chatNotificationEnabled || memberNotificationEnabled);
  }, [chatNotificationEnabled, memberNotificationEnabled]);

  const revertToggles = (notificationType?: NotificationType) => {
    if (notificationType === NotificationType.CHAT_MESSAGE_NEW) {
      setChatNotificationEnabled((prev) => !prev);
    } else if (notificationType === NotificationType.MEMBER_NOTIFICATION_EVENT) {
      setMemberNotificationEnabled((prev) => !prev);
    } else {
      setNotificationsEnabled((prev) => !prev);
      setChatNotificationEnabled((prev) => !prev);
      setMemberNotificationEnabled((prev) => !prev);
    }
  };

  const onChangeNotificationPreferences = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, notificationType?: NotificationType) => {
      if (!memberUuid) {
        onErrorCallback();
        return;
      }
      if (notificationType === NotificationType.CHAT_MESSAGE_NEW) {
        setChatNotificationEnabled(event.target.checked);
      } else if (notificationType === NotificationType.MEMBER_NOTIFICATION_EVENT) {
        setMemberNotificationEnabled(event.target.checked);
      } else {
        setNotificationsEnabled(event.target.checked);
        setChatNotificationEnabled(event.target.checked);
        setMemberNotificationEnabled(event.target.checked);
      }
      try {
        const response = await updateNotificationsPreferences({
          memberUuid,
          body: {
            notification_channel: NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
            ...(notificationType ? { notification_type: notificationType } : {}),
            ...(!memberSmsEnabled && !notificationType
              ? { notification_type: NotificationType.CHAT_MESSAGE_NEW }
              : {}),
            enabled: event.target.checked,
          },
        });

        if (response?.error) {
          revertToggles(notificationType);
          onErrorCallback();
          return;
        }

        onSuccessCallback?.();
      } catch (error) {
        revertToggles(notificationType);
        onErrorCallback();
      }
    },
    [setNotificationsEnabled, memberUuid, memberSmsEnabled, updateNotificationsPreferences],
  );

  return {
    notificationsEnabled,
    chatNotificationEnabled,
    memberNotificationEnabled,
    onChangeNotificationPreferences,
    isUpdateNotificatonPreferencesLoading: isLoading,
    updateNotificationsPreferencesError,
  };
};
